import { useEffect, useRef } from 'react';

import type { ReactNode } from 'react';

export interface DrawerBaseProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  width?: number;
}

export function DrawerBase({
  open,
  onClose,
  title,
  children,
  footer,
  width = 420,
}: DrawerBaseProps): React.JSX.Element | null {
  const panelRef = useRef<HTMLElement>(null);
  // Ao abrir, move o foco PARA o dialog (a11y de modal) — assim o card disparador
  // (role="button") perde o foco e o anel dourado de :focus-visible não fica "preso"
  // no card atrás do drawer. Ao fechar (unmount), o foco cai no body (sem anel órfão).
  useEffect(() => {
    if (open) panelRef.current?.focus();
  }, [open]);
  if (!open) return null;
  // .drawer-back é o flex-container que ancora .drawer à direita (compounds-3.css:
  // .drawer não tem position própria — depende de estar DENTRO de .drawer-back).
  // Mantemos a estrutura aninhada do mockup; o backdrop fecha no clique/Escape e o
  // dialog tem botão de fechar explícito (a11y preservada).
  return (
    <div
      className="drawer-back"
      role="presentation"
      onClick={(e) => {
        // fecha só no clique direto no backdrop (não em cliques que borbulham do drawer)
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <aside
        ref={panelRef}
        tabIndex={-1}
        className="drawer"
        style={{ width, outline: 'none' }}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <header className="drawer__head">
          <h2 className="drawer__title">{title}</h2>
          <button
            type="button"
            className="btn btn--ghost btn--sm btn--icon"
            onClick={onClose}
            aria-label="Fechar"
          >
            ×
          </button>
        </header>
        <div className="drawer__body">{children}</div>
        {footer !== undefined && <footer className="drawer__foot">{footer}</footer>}
      </aside>
    </div>
  );
}
